'use client'

import { initializeApp, getApps, FirebaseApp } from 'firebase/app'
import { getAuth, Auth, GoogleAuthProvider, signInWithPopup, signOut as firebaseSignOut, onIdTokenChanged, User, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, updateProfile } from 'firebase/auth'
import { getFirestore, Firestore, doc, getDoc, setDoc, updateDoc, deleteDoc, serverTimestamp, increment, collection, query, where, getDocs, onSnapshot } from 'firebase/firestore'
import type { 
  PlannerCard, 
  PlannerStatus, 
  RecentSearch,
  UserPreferences,
  UserSubscription,
  DealbreakersConfig,
  NotificationPreferences,
  CardDensity,
  ViewMode,
  SortOption,
  Theme,
  Country,
  ListingFreshness,
  BoardSharing,
  BoardCollaborator,
  SharedBoardAccess,
  BoardCollaboratorRole,
} from './types'
import { DEFAULT_SUBSCRIPTION } from './types'

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
}

// Initialize Firebase
let app: FirebaseApp
let auth: Auth
let db: Firestore

if (typeof window !== 'undefined') {
  if (!getApps().length) {
    app = initializeApp(firebaseConfig)
  } else {
    app = getApps()[0]
  }
  auth = getAuth(app)
  db = getFirestore(app)
}

// Google Auth Provider
const googleProvider = new GoogleAuthProvider()

// Auth functions
export async function signInWithGoogle() {
  try {
    const result = await signInWithPopup(auth, googleProvider)
    const user = result.user
    
    // Create or update user profile in Firestore
    await createOrUpdateUserProfile(user)
    
    return user
  } catch (error) {
    console.error('Google sign-in error:', error)
    throw error
  }
}

export async function signInWithEmail(email: string, password: string) {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password)
    await createOrUpdateUserProfile(result.user)
    return result.user
  } catch (error) {
    console.error('Email sign-in error:', error)
    throw error
  }
}

export async function signUpWithEmail(email: string, password: string, displayName: string) {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password)
    await updateProfile(result.user, { displayName })
    await createOrUpdateUserProfile(result.user)
    return result.user
  } catch (error) {
    console.error('Email sign-up error:', error)
    throw error
  }
}

export async function resetPassword(email: string) {
  try {
    await sendPasswordResetEmail(auth, email)
  } catch (error) {
    console.error('Password reset error:', error)
    throw error
  }
}

export async function signOut() {
  try {
    await firebaseSignOut(auth)
  } catch (error) {
    console.error('Sign out error:', error)
    throw error
  }
}

export function onIdTokenChange(callback: (user: User | null) => void) {
  return onIdTokenChanged(auth, callback)
}

export async function getIdToken(forceRefresh: boolean = false, user?: User | null): Promise<string | null> {
  // Use provided user or fall back to auth.currentUser
  const targetUser = user ?? auth.currentUser
  if (!targetUser) return null
  
  try {
    return await targetUser.getIdToken(forceRefresh)
  } catch (error) {
    console.error('Error getting ID token:', error)
    return null
  }
}

// Firestore functions
export async function createOrUpdateUserProfile(user: User) {
  const userRef = doc(db, 'users', user.uid)
  const userSnap = await getDoc(userRef)
  
  if (!userSnap.exists()) {
    // Create new user profile
    // Note: savedSearches are now stored in Elasticsearch via the backend API
    await setDoc(userRef, {
      email: user.email,
      displayName: user.displayName,
      photoURL: user.photoURL,
      country: 'CZ',
      language: 'en',
      theme: 'system',
      createdAt: serverTimestamp(),
      lastLoginAt: serverTimestamp(),
      savedListings: [],
      subscription: { ...DEFAULT_SUBSCRIPTION },
    })
  } else {
    // Update last login
    await updateDoc(userRef, {
      lastLoginAt: serverTimestamp(),
      // Update profile info if changed
      displayName: user.displayName,
      photoURL: user.photoURL,
    })
  }
}

export async function getUserProfile(uid: string) {
  const userRef = doc(db, 'users', uid)
  const userSnap = await getDoc(userRef)
  
  if (userSnap.exists()) {
    return userSnap.data()
  }
  return null
}

// Default preferences for new users or missing fields
export const DEFAULT_USER_PREFERENCES: Omit<UserPreferences, 'savedSearches' | 'savedListings'> & { subscription: UserSubscription } = {
  country: 'CZ',
  language: 'en',
  theme: 'system',
  defaultViewMode: 'list',
  cardDensity: 'comfortable',
  defaultCity: 'Praha',
  defaultSort: 'newest',
  defaultPriceMode: true,
  listingFreshness: null,
  dealbreakers: { mustHave: [] },
  aiSummaryLanguage: 'cs',
  notifications: {
    email: true,
    push: false,
    whatsapp: false,
    frequency: 'daily',
    quietHoursEnabled: false,
    quietHoursStart: '22:00',
    quietHoursEnd: '08:00',
  },
  searchHistoryEnabled: true,
  subscription: { ...DEFAULT_SUBSCRIPTION },
}

export async function updateUserPreferences(uid: string, preferences: Partial<Omit<UserPreferences, 'savedSearches' | 'savedListings'>>) {
  const userRef = doc(db, 'users', uid)
  // Flatten nested objects into dot-notation for partial updates
  // e.g. { notifications: { email: false } } needs special handling
  // We use a simple approach: pass the object directly and let Firestore merge
  await updateDoc(userRef, preferences as Record<string, any>)
}

// Navigation targets (commute places) persist to the user doc — the same
// client-Firestore pattern dealbreakers use, so a user's tracked destinations
// survive re-login across web devices instead of living only in localStorage.
export async function updateUserCommutePlaces(uid: string, places: Array<Record<string, unknown>>) {
  const userRef = doc(db, 'users', uid)
  // Firestore rejects `undefined` — drop unset fields (e.g. lat/lon before a
  // target has geocoded) from each place before writing.
  const clean = (Array.isArray(places) ? places : []).map((p) => {
    const o: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(p ?? {})) if (v !== undefined) o[k] = v
    return o
  })
  // setDoc(merge) — not updateDoc — so a write that races ahead of first
  // sign-up profile creation creates the doc instead of throwing "No document
  // to update".
  await setDoc(userRef, { commutePlaces: clean }, { merge: true })
}

export async function getUserPreferences(uid: string): Promise<Omit<UserPreferences, 'savedSearches' | 'savedListings'>> {
  const profile = await getUserProfile(uid)
  if (!profile) return { ...DEFAULT_USER_PREFERENCES }
  
  return {
    country: profile.country || DEFAULT_USER_PREFERENCES.country,
    language: profile.language || DEFAULT_USER_PREFERENCES.language,
    theme: profile.theme || DEFAULT_USER_PREFERENCES.theme,
    defaultViewMode: profile.defaultViewMode || DEFAULT_USER_PREFERENCES.defaultViewMode,
    cardDensity: profile.cardDensity || DEFAULT_USER_PREFERENCES.cardDensity,
    defaultCity: profile.defaultCity || DEFAULT_USER_PREFERENCES.defaultCity,
    defaultSort: profile.defaultSort || DEFAULT_USER_PREFERENCES.defaultSort,
    defaultPriceMode: profile.defaultPriceMode ?? DEFAULT_USER_PREFERENCES.defaultPriceMode,
    listingFreshness: profile.listingFreshness ?? DEFAULT_USER_PREFERENCES.listingFreshness,
    dealbreakers: profile.dealbreakers || DEFAULT_USER_PREFERENCES.dealbreakers,
    aiSummaryLanguage: profile.aiSummaryLanguage || DEFAULT_USER_PREFERENCES.aiSummaryLanguage,
    notifications: {
      ...DEFAULT_USER_PREFERENCES.notifications,
      ...(profile.notifications || {}),
    },
    searchHistoryEnabled: profile.searchHistoryEnabled ?? DEFAULT_USER_PREFERENCES.searchHistoryEnabled,
    subscription: {
      ...DEFAULT_SUBSCRIPTION,
      ...(profile.subscription || {}),
    },
  }
}

/** Get subscription data for a user. */
export async function getUserSubscription(uid: string): Promise<UserSubscription> {
  const profile = await getUserProfile(uid)
  if (!profile || !profile.subscription) return { ...DEFAULT_SUBSCRIPTION }
  return {
    ...DEFAULT_SUBSCRIPTION,
    ...profile.subscription,
  }
}

/**
 * Subscribe to real-time changes on the user's subscription field.
 * Returns an unsubscribe function.
 */
export function onSubscriptionChange(
  uid: string,
  callback: (subscription: UserSubscription) => void
): () => void {
  const userRef = doc(db, 'users', uid)
  return onSnapshot(userRef, (snapshot) => {
    if (snapshot.exists()) {
      const data = snapshot.data()
      callback({
        ...DEFAULT_SUBSCRIPTION,
        ...(data.subscription || {}),
      })
    }
  })
}

export async function exportUserData(uid: string): Promise<Record<string, any>> {
  const profile = await getUserProfile(uid)
  if (!profile) return {}
  
  // Return all user data for GDPR export
  return {
    profile: {
      email: profile.email,
      displayName: profile.displayName,
      createdAt: profile.createdAt,
    },
    preferences: await getUserPreferences(uid),
    savedListings: profile.savedListings || [],
    plannerCards: profile.plannerCards || [],
    recentSearches: profile.recentSearches || [],
  }
}

// NOTE: saveSearch and deleteSearch functions have been moved to the backend API
// Use api.createSavedSearch() and api.deleteSavedSearch() from '@/lib/api' instead

// Saved Listings functions
export async function saveListing(uid: string, listingId: string) {
  const userRef = doc(db, 'users', uid)
  const userSnap = await getDoc(userRef)
  
  if (userSnap.exists()) {
    const userData = userSnap.data()
    const savedListings = userData.savedListings || []
    
    // Check if already saved
    if (savedListings.some((l: any) => l.id === listingId)) {
      return
    }
    
    await updateDoc(userRef, {
      savedListings: [...savedListings, { id: listingId, savedAt: new Date().toISOString() }]
    })
  }
}

export async function unsaveListing(uid: string, listingId: string) {
  const userRef = doc(db, 'users', uid)
  const userSnap = await getDoc(userRef)
  
  if (userSnap.exists()) {
    const userData = userSnap.data()
    const savedListings = (userData.savedListings || []).filter(
      (l: any) => l.id !== listingId
    )
    
    await updateDoc(userRef, { savedListings })
  }
}

export async function getSavedListingIds(uid: string): Promise<string[]> {
  const userRef = doc(db, 'users', uid)
  const userSnap = await getDoc(userRef)

  if (userSnap.exists()) {
    const userData = userSnap.data()
    return (userData.savedListings || []).map((l: any) => l.id)
  }
  return []
}

export interface SavedListingRef {
  id: string
  /** ISO timestamp of when it was liked; missing on legacy records. */
  savedAt?: string
}

/** Saved listings WITH their like timestamps (for age-based clearing). */
export async function getSavedListings(uid: string): Promise<SavedListingRef[]> {
  const userRef = doc(db, 'users', uid)
  const userSnap = await getDoc(userRef)
  if (userSnap.exists()) {
    const userData = userSnap.data()
    return (userData.savedListings || []).map((l: any) => ({ id: l.id, savedAt: l.savedAt }))
  }
  return []
}

/** Remove many likes at once; returns the ids that remain saved. */
export async function unsaveListings(uid: string, listingIds: string[]): Promise<string[]> {
  const userRef = doc(db, 'users', uid)
  const userSnap = await getDoc(userRef)
  if (!userSnap.exists()) return []
  const drop = new Set(listingIds)
  const remaining = (userSnap.data().savedListings || []).filter((l: any) => !drop.has(l.id))
  await updateDoc(userRef, { savedListings: remaining })
  return remaining.map((l: any) => l.id)
}

/** Re-add previously removed likes, preserving their original savedAt — the
 * Undo for a bulk clear (skips any that came back in the meantime). */
export async function restoreSavedListings(uid: string, refs: SavedListingRef[]): Promise<void> {
  if (!refs.length) return
  const userRef = doc(db, 'users', uid)
  const userSnap = await getDoc(userRef)
  if (!userSnap.exists()) return
  const current = userSnap.data().savedListings || []
  const have = new Set(current.map((l: any) => l.id))
  const readd = refs
    .filter((r) => !have.has(r.id))
    .map((r) => ({ id: r.id, savedAt: r.savedAt || new Date().toISOString() }))
  if (!readd.length) return
  await updateDoc(userRef, { savedListings: [...current, ...readd] })
}

/** Stable shortlist numbers (#1…X on the Liked page): each home gets a number
 * the first time it's liked and keeps it forever — numbers are never reused
 * or reassigned, so "what about #3?" stays true even after unlikes, re-likes
 * and bulk clears. Assignment is lazy: any saved listing without a number is
 * backfilled here in first-like order (savedAt ascending; legacy entries
 * without a timestamp keep their array position — the array is append-ordered).
 * One users-doc write, only when something new needed a number. */
export async function ensureLikedNumbers(uid: string): Promise<Record<string, number>> {
  const userRef = doc(db, 'users', uid)
  const userSnap = await getDoc(userRef)
  if (!userSnap.exists()) return {}
  const data = userSnap.data()
  const numbers: Record<string, number> = { ...(data.likedNumbers || {}) }
  const saved: SavedListingRef[] = data.savedListings || []
  const missing = saved.filter((l) => l.id && !numbers[l.id])
  if (!missing.length) return numbers
  const ordered = missing
    .map((l, i) => ({ l, i }))
    .sort((a, b) => {
      if (a.l.savedAt && b.l.savedAt) return a.l.savedAt < b.l.savedAt ? -1 : a.l.savedAt > b.l.savedAt ? 1 : a.i - b.i
      if (a.l.savedAt) return 1 // timestamped entries are newer than legacy ones
      if (b.l.savedAt) return -1
      return a.i - b.i
    })
  let next = Math.max(0, ...Object.values(numbers))
  for (const { l } of ordered) numbers[l.id] = ++next
  await updateDoc(userRef, { likedNumbers: numbers })
  return numbers
}

export async function deleteAccount(uid: string) {
  // Delete user document
  const userRef = doc(db, 'users', uid)
  // Note: In production, you'd want to also delete the auth user
  // and any related data. This should be done via a Cloud Function
  // for security reasons.
  
  // For now, just mark the account as deleted
  await updateDoc(userRef, {
    deletedAt: serverTimestamp(),
    email: null,
    displayName: 'Deleted User',
    photoURL: null,
  })
}

// Appreciation counter functions
export async function getAppreciationCount(): Promise<number> {
  try {
    const statsRef = doc(db, 'stats', 'appreciation')
    const statsSnap = await getDoc(statsRef)
    
    if (statsSnap.exists()) {
      return statsSnap.data().count || 0
    }
    return 0
  } catch (error) {
    console.error('Error getting appreciation count:', error)
    return 0
  }
}

export async function incrementAppreciation(): Promise<number> {
  try {
    const statsRef = doc(db, 'stats', 'appreciation')
    const statsSnap = await getDoc(statsRef)
    
    if (statsSnap.exists()) {
      await updateDoc(statsRef, {
        count: increment(1)
      })
    } else {
      await setDoc(statsRef, {
        count: 1,
        createdAt: serverTimestamp()
      })
    }
    
    // Return the new count
    const newSnap = await getDoc(statsRef)
    return newSnap.exists() ? newSnap.data().count : 1
  } catch (error) {
    console.error('Error incrementing appreciation:', error)
    throw error
  }
}

// Planner Card functions

export async function getPlannerCards(uid: string): Promise<PlannerCard[]> {
  const userRef = doc(db, 'users', uid)
  const userSnap = await getDoc(userRef)
  
  if (userSnap.exists()) {
    const userData = userSnap.data()
    return userData.plannerCards || []
  }
  return []
}

export async function updatePlannerCard(uid: string, card: PlannerCard): Promise<void> {
  const userRef = doc(db, 'users', uid)
  const userSnap = await getDoc(userRef)
  
  if (userSnap.exists()) {
    const userData = userSnap.data()
    const plannerCards: PlannerCard[] = userData.plannerCards || []
    
    // Find existing card or add new one
    const existingIndex = plannerCards.findIndex(c => c.listingId === card.listingId)
    
    if (existingIndex >= 0) {
      plannerCards[existingIndex] = { ...card, updatedAt: new Date().toISOString() }
    } else {
      plannerCards.push({
        ...card,
        addedAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })
    }
    
    await updateDoc(userRef, { plannerCards })
  }
}

export async function deletePlannerCard(uid: string, listingId: string): Promise<void> {
  const userRef = doc(db, 'users', uid)
  const userSnap = await getDoc(userRef)
  
  if (userSnap.exists()) {
    const userData = userSnap.data()
    const plannerCards = (userData.plannerCards || []).filter(
      (c: PlannerCard) => c.listingId !== listingId
    )
    
    await updateDoc(userRef, { plannerCards })
  }
}

export async function updatePlannerCards(uid: string, cards: PlannerCard[]): Promise<void> {
  const userRef = doc(db, 'users', uid)
  await updateDoc(userRef, { plannerCards: cards })
}

export async function addListingToPlanner(uid: string, listingId: string, status: PlannerStatus = 'maybe'): Promise<PlannerCard> {
  const newCard: PlannerCard = {
    listingId,
    status,
    rating: null,
    notes: '',
    customTags: [],
    budgetMax: null,
    addedAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  }
  
  await updatePlannerCard(uid, newCard)
  return newCard
}

// Recent Search History functions

const MAX_RECENT_SEARCHES = 20

export async function getRecentSearches(uid: string): Promise<RecentSearch[]> {
  const userRef = doc(db, 'users', uid)
  const userSnap = await getDoc(userRef)
  
  if (userSnap.exists()) {
    const userData = userSnap.data()
    return userData.recentSearches || []
  }
  return []
}

export async function addRecentSearch(uid: string, search: RecentSearch): Promise<void> {
  // Strip undefined values recursively — Firestore rejects undefined at any depth
  const sanitized = JSON.parse(JSON.stringify(search))

  const userRef = doc(db, 'users', uid)
  const userSnap = await getDoc(userRef)
  
  if (userSnap.exists()) {
    const userData = userSnap.data()
    let recentSearches: RecentSearch[] = userData.recentSearches || []
    
    // Deduplicate: remove existing entry with same displayLabel
    recentSearches = recentSearches.filter(
      (s: RecentSearch) => s.displayLabel !== search.displayLabel
    )
    
    // Prepend new search and cap at max
    recentSearches = [sanitized, ...recentSearches].slice(0, MAX_RECENT_SEARCHES)
    
    await updateDoc(userRef, { recentSearches })
  }
}

export async function removeRecentSearch(uid: string, id: string): Promise<void> {
  const userRef = doc(db, 'users', uid)
  const userSnap = await getDoc(userRef)
  
  if (userSnap.exists()) {
    const userData = userSnap.data()
    const recentSearches = (userData.recentSearches || []).filter(
      (s: RecentSearch) => s.id !== id
    )
    
    await updateDoc(userRef, { recentSearches })
  }
}

export async function clearRecentSearches(uid: string): Promise<void> {
  const userRef = doc(db, 'users', uid)
  await updateDoc(userRef, { recentSearches: [] })
}

// Board Sharing functions
//
// Uses a separate `boardShares` collection so the owner never writes to another
// user's document. Each share is stored as:
//
//   boardShares/{ownerUid}_{collaboratorUid}
//
// REQUIRED Firestore Security Rules — apply via Firebase Console or deploy the
// firestore.rules file at the repo root.  See that file for the full ruleset.
//

/**
 * Look up a user by email via the backend API (uses Firebase Admin SDK
 * server-side). This avoids exposing a client-side Firestore query on
 * the users collection which would allow email enumeration.
 */
export async function findUserByEmail(email: string): Promise<{ uid: string; email: string; displayName: string; photoURL: string } | null> {
  // Lazy-import to avoid circular dependency (api.ts → types.ts ← firebase.ts)
  const { api } = await import('./api')
  try {
    return await api.findUserByEmail(email)
  } catch {
    return null
  }
}

/** Get all collaborators the owner has shared their board with. */
export async function getBoardSharing(uid: string): Promise<BoardSharing> {
  const sharesRef = collection(db, 'boardShares')
  const q = query(sharesRef, where('ownerUid', '==', uid))
  const snapshot = await getDocs(q)
  
  if (snapshot.empty) return { isShared: false, collaborators: [] }
  
  const collaborators: BoardCollaborator[] = snapshot.docs.map(d => {
    const data = d.data()
    return {
      uid: data.collaboratorUid,
      email: data.collaboratorEmail,
      displayName: data.collaboratorName,
      photoURL: data.collaboratorPhotoURL,
      role: data.role || 'write',
      addedAt: data.createdAt,
    }
  })
  
  return { isShared: collaborators.length > 0, collaborators }
}

export async function shareBoardWithUser(
  ownerUid: string,
  ownerName: string,
  ownerEmail: string,
  ownerPhotoURL: string,
  collaboratorEmail: string,
  role: BoardCollaboratorRole = 'write'
): Promise<{ success: boolean; error?: string }> {
  try {
    // Find the collaborator by email
    const collaborator = await findUserByEmail(collaboratorEmail)
    if (!collaborator) {
      return { success: false, error: 'User not found. They need to create an account first.' }
    }
    
    if (collaborator.uid === ownerUid) {
      return { success: false, error: 'You cannot share the board with yourself.' }
    }
    
    // Check if already shared (deterministic document ID)
    const shareId = `${ownerUid}_${collaborator.uid}`
    const shareRef = doc(db, 'boardShares', shareId)
    const shareSnap = await getDoc(shareRef)
    
    if (shareSnap.exists()) {
      return { success: false, error: 'This user already has access to your board.' }
    }
    
    // Create the share document (owner only writes to boardShares collection)
    await setDoc(shareRef, {
      ownerUid,
      ownerName,
      ownerEmail,
      ownerPhotoURL,
      collaboratorUid: collaborator.uid,
      collaboratorEmail: collaborator.email,
      collaboratorName: collaborator.displayName,
      collaboratorPhotoURL: collaborator.photoURL,
      role,
      createdAt: new Date().toISOString(),
    })
    
    return { success: true }
  } catch (error) {
    console.error('Error sharing board:', error)
    return { success: false, error: 'Failed to share board. Please try again.' }
  }
}

export async function removeBoardCollaborator(
  ownerUid: string,
  collaboratorUid: string
): Promise<void> {
  const shareId = `${ownerUid}_${collaboratorUid}`
  const shareRef = doc(db, 'boardShares', shareId)
  await deleteDoc(shareRef)
}

/** Get all boards that have been shared with this user. */
export async function getSharedBoardsForUser(uid: string): Promise<SharedBoardAccess[]> {
  const sharesRef = collection(db, 'boardShares')
  const q = query(sharesRef, where('collaboratorUid', '==', uid))
  const snapshot = await getDocs(q)
  
  return snapshot.docs.map(d => {
    const data = d.data()
    return {
      ownerUid: data.ownerUid,
      ownerName: data.ownerName,
      ownerEmail: data.ownerEmail,
      ownerPhotoURL: data.ownerPhotoURL,
      role: data.role || 'write',
      addedAt: data.createdAt,
    }
  })
}

export async function addListingToSharedBoard(
  ownerUid: string,
  listingId: string,
  status: PlannerStatus = 'maybe'
): Promise<PlannerCard> {
  // Add to owner's savedListings if not already present
  await saveListing(ownerUid, listingId)
  // Add to owner's planner cards
  return addListingToPlanner(ownerUid, listingId, status)
}

export { auth, db }

